"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  BookOpen,
  Rocket,
  LayoutDashboard,
  Users,
  FileText,
  Briefcase,
  Receipt,
  CalendarDays,
  CalendarCheck,
  BarChart3,
  UserCog,
  Globe,
  Settings,
  Workflow,
  Smartphone,
  Keyboard,
  ChevronRight,
} from "lucide-react";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const helpNavigation: NavSection[] = [
  {
    title: "Getting Started",
    items: [
      { title: "Quick Start", href: "/help/getting-started/quick-start", icon: Rocket },
      { title: "Dashboard Overview", href: "/help/getting-started/dashboard-overview", icon: LayoutDashboard },
    ],
  },
  {
    title: "Features",
    items: [
      { title: "Clients", href: "/help/features/clients", icon: Users },
      { title: "Quotes", href: "/help/features/quotes", icon: FileText },
      { title: "Jobs", href: "/help/features/jobs", icon: Briefcase },
      { title: "Invoices", href: "/help/features/invoices", icon: Receipt },
      { title: "Schedule", href: "/help/features/schedule", icon: CalendarDays },
      { title: "Bookings", href: "/help/features/bookings", icon: CalendarCheck },
      { title: "Reports", href: "/help/features/reports", icon: BarChart3 },
      { title: "Team", href: "/help/features/team", icon: UserCog },
      { title: "Client Portal", href: "/help/features/client-portal", icon: Globe },
      { title: "Settings", href: "/help/features/settings", icon: Settings },
    ],
  },
  {
    title: "Guides",
    items: [
      { title: "Complete Workflow", href: "/help/guides/complete-workflow", icon: Workflow },
      { title: "Mobile Tips", href: "/help/guides/mobile-tips", icon: Smartphone },
      { title: "Keyboard Shortcuts", href: "/help/guides/keyboard-shortcuts", icon: Keyboard },
    ],
  },
];

export default function HelpLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  return (
    <div className="flex flex-col lg:flex-row gap-6 min-h-[calc(100vh-8rem)]">
      {/* Sidebar */}
      <aside className="lg:w-64 flex-shrink-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 lg:sticky lg:top-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            <BookOpen className="h-6 w-6 text-info" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Help Center</h2>
          </div>

          {/* Navigation */}
          <nav className="space-y-6">
            {helpNavigation.map((section) => (
              <div key={section.title}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-2">
                  {section.title}
                </h3>
                <ul className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = pathname === item.href;
                    const Icon = item.icon;
                    return (
                      <li key={item.href}>
                        <Link
                          href={item.href}
                          className={cn(
                            "flex items-center gap-2 px-2 py-2 rounded-lg text-sm transition-colors",
                            isActive
                              ? "bg-info/10 dark:bg-blue-900/30 text-info dark:text-info font-medium"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          )}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{item.title}</span>
                          {isActive && (
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
